/* The plate pages, loaded (deferred) only on them: the plate product page (snippets/plate-product.liquid) and the
   plate collection page (snippets/plate-collection.liquid). Each works without this script: the pitch cards and
   thickness options are links to their variants (?variant=), the form posts to /cart/add, the zoom is a link to the
   full-size file, the truss types and the 3D switch are radio buttons and a checkbox. This script makes them better:

   Product page
   - pitch and thickness become radio groups (arrow keys, one tab stop each) that swap the selection in place: the
     price, the figure (in its fixed box), the form's variant, and the URL (history.replaceState, ?variant=);
   - the last pitch and thickness chosen are remembered in localStorage ('tp:plate'), and a plate page opened without
     ?variant= starts on them when the product sells them (an unpitched plate takes only the thickness);
   - the figure's sheet SVG is fetched and inlined, so its text takes the theme's mono type, and it opens in a
     full-screen zoom dialog: wheel, + and -, the keyboard, pinch and drag; vector sheets stay sharp at any zoom;
   - the bolt hole boxes (off until this script turns them on): the hole the plate is cut with (stock, or the order's,
     kept on the cart), and Customize, which opens the wizard (assets/bolt-holes.js); a hole other than stock goes with
     the add as line item properties (the words the customer reads, and the hidden _spec the Forge back office cuts
     by), and the figure's sheet is redrawn with those holes;
   - Add to cart posts to /cart/add.js, then updates the header's cart count and confirms inline, or says what went
     wrong; after a W, F, Y, VB, or VT plate, "Complete your truss" below it offers the truss's other plates that the
     cart (/cart.js) doesn't hold yet, at the pitch and thickness just added (planMore, drawMore; a plate not made at
     that pitch gets no card), and marks a card
     "In your cart" in place when the cart turns out to hold its plate, or a heel card "You chose the VT8" when the
     cart turns out to hold the other heel (markHeld: after the add, and Back from a card's page);
   - "Where it goes" marks this plate on the truss drawing, from the scene's anchors;
   - on a W whose collection has a saddle, "Need to hold a ridge beam?" follows the selection: its link to the saddle
     at this pitch and steel, and the saddle's render beside it (showRidge, in a box of the saddle's frame);
   - on a W4 or W6 Saddle, a change of pitch or steel swaps both versions in its figure (showSaddle: every picture of
     the product has one size, so the swap moves nothing, the remembered pitch's at load included), and the view
     switch's choice is mirrored on the page as data-view (CSS shows that version, in the figure and on the cards, with
     :has() too, so the switch works without this script). The version is never sent with the add: it is settled with
     the customer after the order. "Complete your truss" follows a saddle as it does a W, and offers the W too, its
     card saying it is for the truss's other face when the ridge beam ends at the plate (data.more.line).

   Collection page
   - every plate on the truss drawings gets a hotspot from the scene's anchors (assets/kit-<scene>.json): a link to
     that plate in this collection, or, on a heel, the heel choice in a dialog;
   - the other truss types' drawings load once the page has loaded and the truss is in view.

   Nothing reads the scroll position, and nothing here animates. */
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const json = el => { try { return JSON.parse(el?.textContent || 'null'); } catch { return null; } };

  /* ---------- the remembered choice: { pitch: '812', thick: '14' } ---------- */
  const KEY = 'tp:plate';
  const recall = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };
  const remember = o => { try { localStorage.setItem(KEY, JSON.stringify({ ...recall(), ...o })); } catch { /* private mode */ } };

  /* ---------- kit scenes: manifests and plate anchors ---------- */
  const manifests = new Map();
  const manifest = url => {
    if (!manifests.has(url)) manifests.set(url, fetch(url).then(r => (r.ok ? r.json() : null)).catch(() => null));
    return manifests.get(url);
  };
  // Each plate's anchor (plate-heel-left, plate-peak, ...; never a -centroid) with its plate code: from its label where
  // the manifest keeps it ("VB4 left heel (vb4-1212): ...", "VB: VB4 left heel ..."), else from the callout at it
  // (its `plate`, "VB4"; an export from before the callouts were named kept the code at the start of its text, "VB4
  // heel"). An anchor with neither is left out.
  function plateAnchors(m) {
    const at = new Map((m.callouts || []).filter(c => c.at && (c.plate || c.text)).map(c => [c.at, c.plate || c.text]));
    return (m.anchors || []).filter(a => /^plate-/.test(a.id) && !/-centroid$/.test(a.id) && !a.hidden).map(a => {
      const text = a.label ? a.label.replace(/^[A-Z]+:\s*/, '') : at.get(a.id) || '';
      const code = (text.match(/^[A-Z]+\d+/) || [])[0];
      return code ? { id: a.id, x: a.x, y: a.y, code } : null;
    }).filter(Boolean);
  }
  // A layer over a kit drawing (snippets/drawing.liquid), contained in its box exactly as the drawing is
  function layer(fig, m, cls) {
    const box = fig.querySelector('.dwg-in');
    if (!box) return null;
    let l = box.querySelector(`.hs.${cls}`);
    if (!l) {
      l = document.createElement('div');
      l.className = `hs ${cls}`;
      l.style.setProperty('--ar', `${m.width} / ${m.height}`);
      box.append(l);
    }
    l.replaceChildren();
    return l;
  }
  const place = (el, a, m) => {
    el.style.setProperty('--x', `${(a.x / m.width) * 100}%`);
    el.style.setProperty('--y', `${(a.y / m.height) * 100}%`);
  };

  /* ---------- dialogs: focus returns to what opened them; a click on the backdrop closes ---------- */
  function dialog(dlg) {
    let opener = null;
    dlg.addEventListener('close', () => { opener?.focus?.({ preventScroll: true }); opener = null; dlg.dispatchEvent(new Event('closed')); });
    dlg.addEventListener('click', e => {
      if (e.target.closest('[data-close], [data-z="close"]')) { dlg.close(); return; }
      if (e.target !== dlg) return;
      const r = dlg.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dlg.close();
    });
    return {
      open(from) {
        opener = from || document.activeElement;
        if (!dlg.open) dlg.showModal();
      },
    };
  }

  /* ---------- the zoom dialog: pan and zoom one picture ---------- */
  function zoomer(dlg) {
    const stage = $('[data-z-stage]', dlg), item = $('[data-z-item]', dlg), level = $('[data-z-level]', dlg);
    const title = $('[data-zoom-title]', dlg), zin = $('[data-z="in"]', dlg), zout = $('[data-z="out"]', dlg);
    const base = dialog(dlg);
    const MAX = 12;
    let ar = 1, k = 1, x = 0, y = 0, bw = 0, bh = 0;
    const size = () => { const W = stage.clientWidth, H = stage.clientHeight; bw = Math.min(W, H * ar); bh = bw / ar; };
    function apply() {
      const W = stage.clientWidth, H = stage.clientHeight, w = bw * k, h = bh * k;
      // a picture smaller than the stage stays centered; a larger one can't be pulled clear of it
      x = w <= W ? (W - w) / 2 : Math.min(0, Math.max(W - w, x));
      y = h <= H ? (H - h) / 2 : Math.min(0, Math.max(H - h, y));
      // the element itself is resized (not scaled), so an SVG is redrawn sharp at every zoom
      item.style.width = `${w}px`;
      item.style.height = `${h}px`;
      item.style.transform = `translate(${x}px, ${y}px)`;
      level.value = `${Math.round(k * 100)}%`;
      zout.disabled = k <= 1.001;
      zin.disabled = k >= MAX - 0.001;
    }
    function zoomAt(nk, cx, cy) {
      nk = Math.min(MAX, Math.max(1, nk));
      const f = nk / k;
      x = cx - (cx - x) * f;
      y = cy - (cy - y) * f;
      k = nk;
      apply();
    }
    const center = () => [stage.clientWidth / 2, stage.clientHeight / 2];
    const fit = () => { k = 1; size(); apply(); };

    zin.addEventListener('click', () => zoomAt(k * 1.6, ...center()));
    zout.addEventListener('click', () => zoomAt(k / 1.6, ...center()));
    $('[data-z="fit"]', dlg).addEventListener('click', fit);
    stage.addEventListener('wheel', e => {
      e.preventDefault();
      const r = stage.getBoundingClientRect(), d = e.deltaY * (e.deltaMode === 1 ? 32 : e.deltaMode === 2 ? 400 : 1);
      zoomAt(k * Math.exp(-d * 0.0022), e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });
    stage.addEventListener('dblclick', e => {
      const r = stage.getBoundingClientRect();
      if (k > 1.001) fit(); else zoomAt(3, e.clientX - r.left, e.clientY - r.top);
    });
    // one pointer drags; two pinch about their midpoint
    const pts = new Map();
    let start = null;
    const snap = () => {
      const r = stage.getBoundingClientRect(), p = [...pts.values()];
      const mid = p.length > 1 ? [(p[0].x + p[1].x) / 2 - r.left, (p[0].y + p[1].y) / 2 - r.top] : [p[0].x - r.left, p[0].y - r.top];
      start = { k, x, y, mid, d: p.length > 1 ? Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y) : 0 };
    };
    stage.addEventListener('pointerdown', e => {
      if (e.button > 0) return;
      stage.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      stage.classList.add('is-dragging');
      snap();
    });
    stage.addEventListener('pointermove', e => {
      if (!pts.has(e.pointerId) || !start) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const r = stage.getBoundingClientRect(), p = [...pts.values()];
      if (p.length === 1) {
        x = start.x + (p[0].x - r.left - start.mid[0]);
        y = start.y + (p[0].y - r.top - start.mid[1]);
        apply();
      } else {
        const mid = [(p[0].x + p[1].x) / 2 - r.left, (p[0].y + p[1].y) / 2 - r.top];
        const d = Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
        const nk = Math.min(MAX, Math.max(1, start.k * (start.d ? d / start.d : 1)));
        // the drawing point under the starting midpoint follows the midpoint
        x = mid[0] - ((start.mid[0] - start.x) / start.k) * nk;
        y = mid[1] - ((start.mid[1] - start.y) / start.k) * nk;
        k = nk;
        apply();
      }
    });
    const up = e => {
      if (!pts.delete(e.pointerId)) return;
      if (pts.size) snap(); else { start = null; stage.classList.remove('is-dragging'); }
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    dlg.addEventListener('keydown', e => {
      if (e.target.closest('button') && (e.key === 'Enter' || e.key === ' ')) return;
      const step = 60, keys = {
        '+': () => zoomAt(k * 1.6, ...center()), '=': () => zoomAt(k * 1.6, ...center()),
        '-': () => zoomAt(k / 1.6, ...center()), _: () => zoomAt(k / 1.6, ...center()), 0: fit,
        ArrowLeft: () => { x += step; apply(); }, ArrowRight: () => { x -= step; apply(); },
        ArrowUp: () => { y += step; apply(); }, ArrowDown: () => { y -= step; apply(); },
      };
      if (keys[e.key] && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); keys[e.key](); }
    });
    new ResizeObserver(() => { if (dlg.open) { size(); apply(); } }).observe(stage);
    dlg.addEventListener('closed', () => { item.replaceChildren(); pts.clear(); start = null; });
    return {
      open(content, name, ratio, from, alt) {
        item.replaceChildren(content);
        title.textContent = name;
        stage.setAttribute('aria-label', alt || name);
        ar = ratio > 0 ? ratio : 1;
        base.open(from);
        fit();
        stage.focus({ preventScroll: true });
      },
    };
  }

  /* ---------- radio groups over links ---------- */
  // The links stay links (their href is the no-JS path); with JS each group is a radiogroup with one tab stop, arrow
  // keys move and choose, and Space or Enter chooses. A disabled option (aria-disabled) is skipped.
  function radios(group, onPick) {
    group.setAttribute('role', 'radiogroup');
    const items = () => $$(':scope > a', group);
    const usable = () => items().filter(a => a.getAttribute('aria-disabled') !== 'true');
    for (const a of items()) {
      a.setAttribute('role', 'radio');
      a.addEventListener('click', e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        if (a.getAttribute('aria-disabled') !== 'true') onPick(a);
      });
      a.addEventListener('keydown', e => {
        const list = usable(), i = list.indexOf(a);
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = list[(i + 1) % list.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = list[(i - 1 + list.length) % list.length];
        else if (e.key === ' ') { e.preventDefault(); if (i >= 0) onPick(a); return; }
        if (next) { e.preventDefault(); onPick(next); next.focus(); }
      });
    }
    return {
      // checked: the chosen link; every other loses its tab stop
      set(checked) {
        const list = items();
        for (const a of list) {
          const on = a === checked;
          a.removeAttribute('aria-current');
          a.setAttribute('aria-checked', String(on));
          a.tabIndex = on ? 0 : -1;
        }
        if (!checked && list.length) usable()[0]?.setAttribute('tabindex', '0');
      },
    };
  }

  /* ---------- the plate product page ---------- */
  function product(root) {
    const data = json($('script[data-plate-json]', root));
    const form = $('.pp-form', root);
    if (!data || !form) return;
    const idInput = $('[data-id]', form), qty = $('.pp-q', form), add = $('[data-add]', form);
    const addLabel = $('[data-add-label]', form), msg = $('[data-msg]', form), price = $('[data-price]', root);
    const pitchLabel = $('[data-pitch-label]', root), cards = $('[data-group="pitch"]', root), thick = $('[data-group="thick"]', root);
    const fig = $('[data-fig]', root), figLink = $('.pp-fig-a', fig || root), cap = $('[data-cap]', root);
    const ridge = data.ridge && $('[data-ridge]', root);
    const ridgeImg = data.ridge?.img && $('[data-ridge-img]', root);
    const variants = data.variants;
    let more = null; // "Complete your truss", made on the first add (drawMore)
    let cur = variants.find(v => String(v.id) === idInput.value) || variants[0];
    const find = (pitch, th) => variants.find(v => v.pitch === pitch && v.thick === th);
    const best = (pitch, th) => find(pitch, th) || variants.find(v => v.pitch === pitch && v.available) || variants.find(v => v.pitch === pitch);
    const urlOf = v => { const u = new URL(location.href); u.searchParams.set('variant', v.id); return u.pathname + u.search + u.hash; };
    const shown = p => (p && p !== 'none' ? `${p.slice(0, -2)}/12` : '');

    /* the figure: swap the picture in its box; a sheet is inlined once it has been fetched */
    const svgs = new Map();
    const svgText = url => {
      if (!svgs.has(url)) svgs.set(url, fetch(url).then(r => (r.ok ? r.text() : null)).catch(() => null));
      return svgs.get(url);
    };
    async function inline(link, img, f) {
      // the file the img shows (the server's URL for the first figure, the same file as f.src); img.src, not
      // currentSrc, which still names the previous picture until the new one starts loading
      const text = await svgText(img.src || f.src);
      if (!text || link.dataset.src !== f.src) return;
      const doc = new DOMParser().parseFromString(text, 'image/svg+xml'), svg = doc.documentElement;
      if (svg.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) return;
      svg.querySelectorAll('script, foreignObject').forEach(n => n.remove());
      for (const n of [svg, ...svg.querySelectorAll('*')]) for (const at of [...n.attributes]) if (/^on/i.test(at.name)) n.removeAttribute(at.name);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      // the sheet names itself (its size, holes, and file); else the figure's alt
      svg.setAttribute('role', 'img');
      if (!svg.getAttribute('aria-label')) svg.setAttribute('aria-label', f.alt);
      svg.querySelector(':scope > title')?.remove();
      svg.classList.add('pp-svg');
      link.querySelector('svg')?.remove();
      link.append(document.importNode(svg, true));
      img.hidden = true;
      drawHoles();
    }
    const none = $('[data-fig-none]', root);
    function showFigure(f) {
      if (!f || !figLink) return;
      figLink.querySelector('svg')?.remove();
      // a pitch with no drawing at all: the note in the box, no picture, no zoom
      const has = !!f.src;
      figLink.hidden = !has;
      if (none) { none.hidden = has; none.textContent = f.none || ''; }
      root.classList.toggle('pp-nofig', !has);
      if (cap) cap.textContent = f.cap;
      if (!has) { figLink.removeAttribute('href'); figLink.dataset.src = ''; return; }
      let img = $('img', figLink);
      if (!img) { img = document.createElement('img'); img.className = 'pp-fig-img'; img.decoding = 'async'; figLink.append(img); }
      img.hidden = false;
      figLink.href = f.zoom;
      figLink.dataset.kind = f.kind;
      figLink.dataset.title = f.title;
      figLink.dataset.src = f.src;
      // the server already drew the first figure: keep its img (same file) rather than request it again
      const path = u => { try { return new URL(u, location.href).pathname; } catch { return u; } };
      if (path(img.getAttribute('src') || '') !== path(f.src)) {
        if (f.srcset) { img.srcset = f.srcset; img.sizes = '(min-width: 900px) 56vw, 100vw'; } else { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
        img.src = f.src;
      }
      if (f.w && f.h) { img.width = f.w; img.height = f.h; }
      img.alt = f.alt;
      if (f.kind === 'sheet') inline(figLink, img, f);
    }

    /* a saddle's figure (snippets/saddle-figure.liquid): both versions of the selected pitch and steel, each a picture of
       the product's frame size (tools/saddle-to-theme.mjs; one smaller, from an earlier export, at its share of it), so
       a swap moves nothing; the view switch shows one (CSS :has(), and data-view on the page for browsers without it) */
    const S = data.saddle;
    const sFrame = S && $('.ps-frame', root);
    function showSaddle(v) {
      if (!sFrame) return;
      const key = String(data.plate).toLowerCase(), [fw, fh] = S.frame;
      for (const view of ['end', 'thru']) {
        const slot = $(`.ps-v.is-${view}`, sFrame), r = S.renders[`${key}-${v.pitch}-${v.thick}-${view}`];
        if (!slot) continue;
        let img = $('img', slot);
        const note = $('.ps-none', slot);
        if (note) note.hidden = !!r;
        if (!r) { img?.remove(); continue; }
        if (!img) {
          img = document.createElement('img');
          img.className = 'ps-img';
          img.decoding = 'async';
          img.sizes = $('.ps-img', sFrame)?.sizes || `(min-width: 560px) ${Math.round(fw)}px, 90vw`;
          slot.prepend(img);
        }
        const [srcset, w, h] = r, src = srcset.split(' ')[0];
        if (img.getAttribute('src') !== src) { img.srcset = srcset; img.src = src; }
        img.width = w;
        img.height = h;
        img.style.setProperty('--iw', `${((w / fw) * 100).toFixed(2)}%`);
        img.style.setProperty('--ih', `${((h / fh) * 100).toFixed(2)}%`);
        img.alt = S.alt[view].replace('%p', shown(v.pitch)).replace('%t', S.thick[v.thick] || '');
      }
    }
    /* beside "Need to hold a ridge beam?" on a W: the saddle at this pitch and steel (else the other steel at this
       pitch), the ridge beam ending at the plate. Its box is the saddle's frame and every render has that size, so a
       swap changes only the picture; a pitch the saddle isn't drawn at hides it in the same box */
    function showRidge(v) {
      const R = data.ridge, th = [v.thick, '316', '14'].find(t => R.img[`${v.pitch}-${t}`]);
      const r = th && R.img[`${v.pitch}-${th}`];
      if (!r) { ridgeImg.hidden = true; ridgeImg.alt = ''; return; }
      const [srcset, w, h] = r, src = srcset.split(' ')[0];
      if (ridgeImg.getAttribute('src') !== src) { ridgeImg.srcset = srcset; ridgeImg.src = src; }
      ridgeImg.width = w;
      ridgeImg.height = h;
      ridgeImg.alt = R.alt.replace('%p', shown(v.pitch)).replace('%t', R.thick[th] || '');
      ridgeImg.hidden = false;
    }
    const views = $('[data-views]', root);
    if (views) {
      const sync = () => { const on = $('input:checked', views); root.dataset.view = on ? on.value : 'end'; };
      views.addEventListener('change', sync);
      sync();
    }

    /* bolt holes: the hole the plate is cut with. The first box is the plate's unless the customer says otherwise: the
       stock 11/16" for a 5/8" bolt, or the size kept on the cart for every flat plate on the order (the cart attribute
       _bolt_holes, which the page was rendered with). Customize opens the wizard (assets/bolt-holes.js); its answer for
       this plate alone shows in that box, and an answer for the order replaces the first box's. It is no variant: a hole
       other than stock rides on the cart line as properties, the words the customer reads and the _spec the Forge back
       office reads (docs/configurator-versions.md: stock-plate, version 2, the hole's diameter in decimal inches);
       stock sends none, so a stock plate's line is the line it always was. The figure's sheet is drawn in inches, so
       its holes are redrawn at the size chosen, and it says so. */
    const holeBox = $('[data-holes]', root), H = data.holes;
    const STOCK = { d: 0.6875, hole: '11/16"', bolt: '5/8"', stock: true };
    const kept = holeBox && Number(holeBox.dataset.orderD) ? Number(holeBox.dataset.orderD) : null;
    let orderHole = kept ? { d: kept, hole: holeBox.dataset.orderValue.split(' (')[0], value: holeBox.dataset.orderValue, stock: false } : null;
    let ownHole = null; // this plate's alone
    const holeNow = () => ownHole || orderHole || STOCK;
    const [defBox, ownBox] = holeBox ? [$('[data-hole-default]', holeBox), $('[data-hole-custom]', holeBox)] : [];
    const boxSays = (box, v, tag, n) => { $('[data-hole-v]', box).textContent = v; $('[data-hole-tag]', box).textContent = tag; $('[data-hole-n]', box).textContent = n; };
    function showHoles() {
      if (!holeBox || !H) return;
      if (orderHole) boxSays(defBox, H.hole.replace('%s', orderHole.hole), H.order, orderHole.value);
      else boxSays(defBox, H.bolt.replace('%s', STOCK.bolt), H.stock, H.hole.replace('%s', STOCK.hole));
      if (ownHole) boxSays(ownBox, H.hole.replace('%s', ownHole.hole), H.thisPlate, ownHole.stock ? H.stock : ownHole.value);
      else boxSays(ownBox, H.customize, '', H.customizeN);
      defBox.setAttribute('aria-pressed', String(!ownHole));
      ownBox.setAttribute('aria-pressed', String(!!ownHole));
      drawHoles();
    }
    function drawHoles() {
      const svg = figLink && $('svg', figLink);
      if (!svg || !holeBox) return;
      const to = holeNow(), from = Number(svg.dataset.hole || STOCK.d), r = to.d / 2, said = `${to.d}|${to.stock ? STOCK.bolt : to.bolt || ''}`;
      if (said === (svg.dataset.said || `${STOCK.d}|${STOCK.bolt}`)) return;
      for (const c of $$('.ps-hole circle', svg)) c.setAttribute('r', r);
      // the outline the on-steel lines are clipped to: each hole two arcs, redrawn about its center
      for (const path of $$('clipPath path', svg)) {
        path.setAttribute('d', path.getAttribute('d').replace(/M([\d.]+) ([\d.]+)A([\d.]+) \3 0 0 1 ([\d.]+) \2A\3 \3 0 0 1 \1 \2Z/g, (m, x1, y, r0, x2) => {
          if (Math.abs(2 * r0 - from) > 0.01) return m;
          const cx = (Number(x1) + Number(x2)) / 2, a = +(cx - r).toFixed(4), b = +(cx + r).toFixed(4);
          return `M${a} ${y}A${r} ${r} 0 0 1 ${b} ${y}A${r} ${r} 0 0 1 ${a} ${y}Z`;
        }));
      }
      // and the words: the note beside the holes ('DIA 11/16"', '9 holes, for 5/8" rod') and the sheet's own label, by
      // the words around the sizes (a plate's own dimensions can read 11/16" too, as in 1'-9 11/16"). A screw's holes
      // name no bolt.
      const say = text => {
        const t = text.replace(`DIA ${STOCK.hole}`, `DIA ${to.hole}`).replace(`holes ${STOCK.hole}`, `holes ${to.hole}`);
        const bolt = to.stock ? STOCK.bolt : to.bolt;
        return bolt ? t.replace(`for ${STOCK.bolt}`, `for ${bolt}`) : t.replace(/,?\s*for 5\/8"( rod)?/, '');
      };
      for (const t of $$('.ps-note tspan', svg)) { t.dataset.text ??= t.textContent; t.textContent = say(t.dataset.text); }
      svg.dataset.label ??= svg.getAttribute('aria-label') || '';
      svg.setAttribute('aria-label', say(svg.dataset.label));
      svg.dataset.hole = to.d;
      svg.dataset.said = said;
    }
    if (holeBox && H) {
      // the add is this script's from here on, with the holes it shows; the form's own copies are for posting without it
      $$('[data-hole-prop]', holeBox).forEach(i => i.remove());
      const changed = () => { msg.textContent = ''; msg.classList.remove('is-err'); hideMore(); showHoles(); };
      defBox.disabled = ownBox.disabled = false;
      defBox.addEventListener('click', () => { if (ownHole) { ownHole = null; changed(); } });
      ownBox.addEventListener('click', () => window.TPBoltHoles?.open({
        from: ownBox,
        onApply: a => {
          if (a.scope === 'order') { orderHole = a.stock ? null : a; ownHole = null; } else ownHole = a;
          changed();
        },
      }));
      showHoles();
    }

    /* the selection */
    const pitchGroup = cards && radios(cards, a => {
      const v = best(a.dataset.pitch, cur.thick);
      if (v) select(v, true);
    });
    const thickGroup = thick && radios(thick, a => {
      const v = find(cur.pitch, a.dataset.thick);
      if (v) select(v, true);
    });
    function select(v, byUser) {
      const pitchChanged = v.pitch !== cur.pitch || !byUser;
      cur = v;
      idInput.value = v.id;
      if (price) price.innerHTML = v.price;
      if (pitchLabel) pitchLabel.textContent = shown(v.pitch);
      if (cards) {
        let on = null;
        for (const a of $$(':scope > a', cards)) {
          const p = a.dataset.pitch, to = best(p, v.thick);
          if (to) a.href = urlOf(to);
          if (p === v.pitch) on = a;
        }
        pitchGroup.set(on);
      }
      if (thick) {
        let on = null;
        for (const a of $$(':scope > a', thick)) {
          const t = find(v.pitch, a.dataset.thick), p = $('[data-th-price]', a);
          if (t) {
            a.href = urlOf(t);
            a.classList.toggle('is-out', !t.available);
            if (t.available) a.removeAttribute('aria-disabled'); else a.setAttribute('aria-disabled', 'true');
            if (p) p.textContent = t.available ? t.priceShort : `${t.priceShort} · ${data.soldOut}`;
          } else {
            a.removeAttribute('href');
            a.classList.add('is-out');
            a.setAttribute('aria-disabled', 'true');
            if (p) p.textContent = data.notMade;
          }
          if (t === v) on = a;
        }
        thickGroup.set(on);
      }
      add.disabled = !v.available;
      addLabel.textContent = v.available ? data.addLabel : data.soldOut;
      // "Need to hold a ridge beam?": the saddle at this pitch and steel, where it comes that way
      if (ridge) ridge.href = data.ridge.v?.[`${v.pitch}-${v.thick}`] || data.ridge.url;
      if (ridgeImg) showRidge(v);
      if (S) showSaddle(v);
      else if (pitchChanged) showFigure(data.figures[v.pitch]);
      if (byUser) {
        msg.textContent = '';
        msg.classList.remove('is-err');
        hideMore();
        history.replaceState(history.state, '', urlOf(v));
        remember(data.pitched ? { pitch: v.pitch, thick: v.thick } : { thick: v.thick });
      }
    }
    select(cur, false);

    // opened without ?variant=: start on the last pitch and thickness chosen, if this plate comes that way
    if (!new URLSearchParams(location.search).has('variant')) {
      const mem = recall();
      let v = null;
      if (data.pitched && mem.pitch) v = find(mem.pitch, mem.thick || cur.thick) || find(mem.pitch, cur.thick);
      if (!v && mem.thick) v = find(cur.pitch, mem.thick);
      if (v && v !== cur && v.available) select(v, false);
    }

    /* zoom */
    const zd = $('[data-zoom-dialog]', root), zoom = zd && zoomer(zd);
    const openZoom = from => {
      const f = data.figures[cur.pitch] || data.figures[Object.keys(data.figures)[0]];
      if (!f || !f.src || !zoom) return;
      const svg = $('svg', figLink);
      let content, ratio = f.w && f.h ? f.w / f.h : 0;
      if (svg) {
        content = svg.cloneNode(true);
        const vb = svg.viewBox?.baseVal;
        if (vb && vb.width && vb.height) ratio = vb.width / vb.height;
      } else {
        content = new Image();
        content.alt = f.alt;
        content.decoding = 'async';
        // the figure's own picture at once, the full-size one once it has loaded
        content.src = $('img', figLink).currentSrc || f.src;
        if (f.zoom && f.zoom !== content.src) { const big = new Image(); big.onload = () => { content.src = f.zoom; }; big.src = f.zoom; }
      }
      zoom.open(content, f.title, ratio, from, f.alt);
    };
    if (zoom && figLink) {
      figLink.addEventListener('click', e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        openZoom(figLink);
      });
      const btn = $('[data-zoom-btn]', root);
      if (btn) { btn.hidden = false; btn.addEventListener('click', () => openZoom(btn)); }
    }

    /* quantity */
    const clampQty = () => { const n = Math.max(1, Math.min(999, parseInt(qty.value, 10) || 1)); qty.value = n; return n; };
    $$('[data-step]', form).forEach(b => {
      b.disabled = false;
      b.addEventListener('click', () => { qty.value = Math.max(1, Math.min(999, (parseInt(qty.value, 10) || 1) + Number(b.dataset.step))); });
    });
    qty.addEventListener('change', clampQty);

    /* add to cart */
    const cartLink = document.querySelector(`.hdr a[href="${data.cartUrl}"]`);
    function cartCount(n) {
      if (!cartLink) return;
      cartLink.setAttribute('aria-label', n === 1 ? data.cartOne : data.cartOther.replace('1234567', n));
      let badge = $('.ic-n', cartLink);
      if (n > 0) {
        if (!badge) { badge = document.createElement('span'); badge.className = 'ic-n'; badge.setAttribute('aria-hidden', 'true'); cartLink.append(badge); }
        badge.textContent = n;
      } else badge?.remove();
    }
    const esc = s => String(s).replace(/[&<>"']/g, c => `&#${c.charCodeAt(0)};`);

    /* complete your truss: after a W, F, Y, VB, or VT plate goes into the cart, the other plates of the collection's
       king post truss with struts that the cart doesn't hold yet (snippets/plate-truss-json.liquid), each linked to
       its variant at the pitch and thickness just added. A plate is held when the cart has it at that pitch (the F at
       any); a heel at that pitch, VB or VT, holds both heels, and a heel page never offers the other heel. On the F,
       the pitch is the newest of the truss's pitched plates in the cart, else the remembered one.

       No layout shift: the block is laid out the moment Add is clicked, hidden, from the cart's truss lines (as the
       page was rendered, then as /cart.js gave them after each add here), so the room it takes is made while the
       click still accounts for the move (a shift within 500 ms of an input isn't counted). After the add, /cart.js
       confirms it and the block is shown. A failed add leaves the room empty until the next choice takes it away.
       Nothing of it exists before a click, and no card is added, taken away, or redrawn but at one: a plate that the
       cart turns out to hold (added in another tab, or on a card's page before the visitor came Back here, which the
       page's data didn't know) keeps its card, marked "In your cart" in place, still a link to the plate; and since a
       held heel holds both, the other heel's card then says "You chose the VT8" instead of its price (markHeld: the
       price and both marks share one cell, laid out together, so the card keeps its size). The cards keep the pitch
       they were laid out at, the F's too: a cart changed elsewhere to another pitch would need other plates, and
       those would be cards added outside a click. */
    const TR = data.truss, M = data.more;
    const T = Array.isArray(TR?.plates) && M ? TR.plates : null;
    const HEEL = ['VB', 'VT'], ORDER = ['W', 'F', 'Y', 'VB', 'VT'];
    // how many of each one truss takes: the package's plates (a W, an F, two Y, two heels) on both faces of the truss
    const PER_TRUSS = { W: 2, F: 2, Y: 4, VB: 4, VT: 4 };
    // each truss plate's variant: [family, pitch]
    const of = new Map();
    if (T) for (const p of T) for (const x of p.v) of.set(x[0], [p.family, x[1]]);
    const truss = ids => ids.filter(id => of.has(id));
    // the cart's lines of those plates, newest first: as rendered, then as /cart.js says after each add
    let lines = Array.isArray(TR?.cart) ? truss(TR.cart) : [];
    let drawn = null; // the plan the block shows (drawMore)
    function hideMore() { if (more) more.hidden = true; }
    // what the cart (ids) holds at a pitch: a family at that pitch, the F at any
    function holder(ids, pitch) {
      const held = new Set();
      for (const id of ids) {
        const h = of.get(id);
        if (h) { held.add(`${h[0]}:${h[1]}`); held.add(`${h[0]}:*`); }
      }
      return f => held.has(`${f}:${f === 'F' || !pitch ? '*' : pitch}`);
    }
    // the pitch and thickness of the offer after adding v: the pitch added; for the F, the newest pitched truss
    // plate's in the cart, else the remembered one
    function basis(v, ids) {
      let pitch = v.pitch !== 'none' ? v.pitch : '';
      if (!pitch) pitch = ids.map(id => of.get(id)?.[1]).find(q => q && q !== 'none') || recall().pitch || '';
      return { pitch, thick: v.thick };
    }
    // a plate's picture without a pitch: its render at the highest pitch drawn (as the collection's grid draws it),
    // else the store's featured image
    const general = p => p.pics[Object.keys(p.pics).sort((a, b) => (parseInt(b, 10) || 0) - (parseInt(a, 10) || 0))[0]] || p.img;
    // one plate's card at that pitch and thickness
    function card(p, pitch, thick) {
      const flat = p.family === 'F';
      const pick = flat ? p.v.find(x => x[2] === thick) : pitch ? p.v.find(x => x[1] === pitch && x[2] === thick) : null;
      let href = p.url, price = p.from, note = '', pic = general(p);
      if (pick) {
        // that very variant: ?variant= opens it chosen
        href = `${p.url}?variant=${pick[0]}`;
        price = pick[3];
        pic = p.pics[pick[1]] || p.img;
        if (!pick[4]) note = data.soldOut;
      } else if (!flat && !pitch) {
        note = M.pick;
      } else {
        // the pitch is made, the thickness isn't: the pitch's picture is still this plate's
        note = M.notThick.replace('%t', M.thick[thick] || '');
        pic = p.pics[flat ? 'none' : pitch] || p.img;
      }
      return { code: p.code, name: p.name, its: p.its, family: p.family, href, price, note, pic };
    }
    // what to offer after adding v, the cart holding ids: { pitch, thick, cards } (no cards: nothing to offer)
    function planMore(v, ids) {
      const fam = root.dataset.family;
      const { pitch, thick } = basis(v, ids);
      const has = holder(ids, pitch);
      const heelHeld = has('VB') || has('VT');
      // a plate not made at that pitch gets no card (the owner: "dont show anything"; a truss's plates share its pitch)
      const made = p => p.family === 'F' || !pitch || p.v.some(x => x[1] === pitch);
      const list = T.filter(p => p.family !== fam && !has(p.family) && made(p) && !(HEEL.includes(p.family) && (HEEL.includes(fam) || heelHeld)))
        .sort((a, b) => ORDER.indexOf(a.family) - ORDER.indexOf(b.family));
      return { pitch, thick, cards: list.map(p => card(p, pitch, thick)) };
    }
    // mark the shown cards the cart (ids) holds "In your cart" at the cards' pitch, and a heel's card "You chose the
    // collar tie plate" when the cart holds the other heel (a held heel holds both); unmark the others; nothing else changes
    function markHeld(ids) {
      if (!more || !drawn) return;
      const has = holder(ids, drawn.pitch), heel = has('VB') || has('VT');
      for (const a of $$('.pp-sib', more)) {
        const f = a.dataset.family, held = has(f);
        a.classList.toggle('is-in', held);
        a.classList.toggle('is-other', !held && heel && HEEL.includes(f) && !!$('.pp-sib-o', a));
      }
    }
    // back from a card's page (the back/forward cache keeps this one as it was): learn what was added there, mark
    // those cards, and bring the header's count up to date
    if (T) addEventListener('pageshow', e => {
      if (!e.persisted) return;
      fetch(data.cartJs, { headers: { Accept: 'application/json' } }).then(c => c.json())
        .then(c => {
          if (!Array.isArray(c?.items)) return;
          lines = truss(c.items.map(i => i.variant_id));
          markHeld(lines);
          cartCount(c.item_count);
        }).catch(() => {});
    });
    // draw a plan below the form, shown or (until the add is confirmed) holding its room unseen
    function drawMore(plan, show) {
      if (!more) {
        more = document.createElement('div');
        more.className = 'pp-more';
        form.after(more);
      }
      drawn = plan;
      if (!plan.cards.length) { more.hidden = true; return; }
      const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text) n.textContent = text; return n; };
      const h = el('h2', 'lbl-mono pp-lbl pp-more-h', M.heading);
      h.id = M.id;
      h.append(' ', el('span', '', [shown(plan.pitch), M.thick[plan.thick]].filter(Boolean).join(', ')));
      const ul = el('ul', 'plain pp-sibs');
      ul.setAttribute('aria-labelledby', M.id);
      ul.dataset.n = plan.cards.length;
      for (const c of plan.cards) {
        const a = el('a', 'pp-sib');
        a.href = c.href;
        a.dataset.family = c.family;
        const art = el('span', `pp-sib-art${c.pic ? '' : ' is-none'}`);
        if (c.pic) {
          // decoration: the name beside it says which plate
          const img = new Image(64, 64);
          img.alt = '';
          img.decoding = 'async';
          img.src = c.pic;
          art.append(img);
        }
        const t = el('span', 'pp-sib-t');
        // the plate by name, then its code as the part number and, but where a line says what it's for (a saddle's
        // W), how many a truss takes: "Strut plate" over "Part Y4 · 4 per truss", the count on a line of its own on a
        // narrow card (assets/plates.css, .pp-sib-k). (A page rendered before the names: the code over its place.)
        const u = el('span', 'pp-sib-u', M.part ? M.part.replace('%c', c.code) : M.place?.[c.family] || '');
        if (!M.line?.[c.family] && PER_TRUSS[c.family] && M.perTruss) {
          const k = el('span', 'pp-sib-k');
          k.append(el('span', 'pp-sib-d', ' · '), M.perTruss.replace('%n', PER_TRUSS[c.family]));
          u.append(k);
        }
        t.append(el('span', 'pp-sib-c', c.name || c.code), u);
        if (HEEL.includes(c.family)) t.append(el('span', 'pp-sib-l', M.heel[c.family]));
        // a line saying what the plate is for, where the page has one (a saddle's: the W, for the truss's other face)
        else if (M.line?.[c.family]) t.append(el('span', 'pp-sib-l', M.line[c.family]));
        // the price, or (one cell, one size) "In your cart", or on a heel "You chose the" other heel, by name
        const p = el('span', 'pp-sib-p');
        p.append(el('span', 'pp-sib-pr', c.price), el('span', 'pp-sib-in', M.inCart));
        const other = HEEL.includes(c.family) && plan.cards.find(o => HEEL.includes(o.family) && o.family !== c.family);
        if (other && M.otherHeel) p.append(el('span', 'pp-sib-o', M.otherHeel.replace('%c', other.its || other.code)));
        t.append(p);
        if (c.note) t.append(el('span', 'pp-sib-n', c.note));
        a.append(art, t);
        const li = el('li', 'pp-sib-li');
        li.append(a);
        ul.append(li);
      }
      more.replaceChildren(h, ul);
      more.toggleAttribute('data-pending', !show);
      more.hidden = false;
    }

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!cur.available || add.disabled) return;
      const n = clampQty(), v = cur, hole = holeBox ? holeNow() : null, custom = hole && !hole.stock ? hole : null;
      add.disabled = true;
      add.classList.add('is-busy');
      msg.classList.remove('is-err');
      // the confirmation's room, made now: its own words, unseen, so that when they show (after the add returns,
      // past the click's grace) nothing below moves, even where they wrap to a second line
      const added = data.added.replace('%item', esc(`${n} × ${custom ? H.added.replace('%item', v.label).replace('%h', custom.hole) : v.label}`));
      msg.innerHTML = `<span aria-hidden="true" style="visibility:hidden">${added}</span>`;
      // the offer's room, made now (see "complete your truss"), from what the cart is known to hold (this page's own
      // plate never counts: it is never offered)
      let plan = null;
      if (T) {
        try { plan = planMore(v, lines); drawMore(plan, false); } catch { plan = null; hideMore(); }
      }
      // holes other than stock: what the customer reads on the line, and what the Forge back office cuts by
      const item = { id: v.id, quantity: n };
      if (custom) item.properties = { [H.name]: custom.value, _spec: JSON.stringify({ configurator: 'stock-plate', version: 2, hole_diameter: custom.d }) };
      try {
        const r = await fetch(data.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [item] }),
        });
        const res = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(res.description || res.message || data.error);
        const cart = await fetch(data.cartJs, { headers: { Accept: 'application/json' } }).then(c => c.json()).catch(() => null);
        if (cart) cartCount(cart.item_count);
        msg.innerHTML = added;
        // what went into the cart is the choice to remember, as if it had been picked here
        remember(data.pitched ? { pitch: v.pitch, thick: v.thick } : { thick: v.thick });
        // the offer is extra: whatever happens to it, the add stands confirmed. /cart.js has the last word on what
        // the cart holds, but the cards stay the ones laid out at the click, at their pitch: a plate it holds that
        // the page's data didn't know is marked in place (markHeld), and one it no longer holds is not added
        if (T && plan && more) {
          try {
            if (cart) lines = truss(cart.items.map(i => i.variant_id));
            markHeld(lines);
            more.removeAttribute('data-pending');
          } catch { hideMore(); }
        }
      } catch (err) {
        msg.textContent = err instanceof TypeError || !err.message ? data.error : err.message;
        msg.classList.add('is-err');
        // the room made at the click stays, unseen, until the next choice takes it away (a move then is the visitor's)
      } finally {
        add.disabled = !cur.available;
        add.classList.remove('is-busy');
        addLabel.textContent = cur.available ? data.addLabel : data.soldOut;
      }
    });

    /* where it goes: this plate marked on the truss */
    $$('[data-mark]', root).forEach(async a => {
      const dwg = $('.dwg[data-manifest]', a), m = dwg && (await manifest(dwg.dataset.manifest));
      if (!m) return;
      const l = layer(dwg, m, 'hs-marks');
      for (const an of plateAnchors(m).filter(p => p.code === a.dataset.mark)) {
        const s = document.createElement('span');
        s.className = 'hs-mark';
        place(s, an, m);
        l.append(s);
      }
    });
  }

  /* ---------- the plate collection page ---------- */
  function collection(root) {
    const data = json($('script[data-plate-json]', root));
    const plates = data?.plates || {};
    const hd = $('[data-heel-dialog]', root), heel = hd && dialog(hd);

    // every plate on every truss drawing: a link to it here; a heel opens the heel choice
    $$('.plc-fig .dwg[data-manifest]', root).forEach(async fig => {
      const m = await manifest(fig.dataset.manifest);
      if (!m) return;
      const l = layer(fig, m, 'hs-spots');
      for (const an of plateAnchors(m)) {
        const p = plates[an.code];
        if (!p) continue;
        const a = document.createElement('a');
        a.className = 'hs-a';
        a.href = p.url;
        a.setAttribute('aria-label', p.name);
        place(a, an, m);
        if (heel && (p.family === 'VB' || p.family === 'VT')) {
          a.setAttribute('aria-haspopup', 'dialog');
          a.addEventListener('click', e => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
            e.preventDefault();
            heel.open(a);
          });
        }
        l.append(a);
      }
    });

    // the other truss types' drawings are hidden until chosen: load them once the page has loaded and the truss is
    // in view, so a switch shows its drawing at once
    const truss = $('.plc-truss', root);
    if (truss && 'IntersectionObserver' in window) {
      const go = () => $$('.plc-fig img[loading="lazy"]', truss).forEach(i => { i.loading = 'eager'; });
      const io = new IntersectionObserver(es => {
        if (!es.some(e => e.isIntersecting)) return;
        io.disconnect();
        if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
      });
      io.observe(truss);
    }
  }

  const pp = $('[data-plate-product]');
  if (pp) product(pp);
  const plc = $('[data-plate-collection]');
  if (plc) collection(plc);
})();
